import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { usePostHog } from '@posthog/react';
import { setAccessTokenSource } from '../api/client';
import { previewToken } from './previewToken';
import { supabase } from './supabase';

export interface SessionValue {
  session: Session | null;
  /** True until the first answer arrives. Rendering "entre" before it lands logs people out on refresh. */
  loading: boolean;
  /** False when the Supabase project is not configured in this build. */
  configured: boolean;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession precisa estar dentro de SessionProvider');
  }
  return value;
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();

  const client = supabase();

  useEffect(() => {
    const preview = previewToken();
    if (preview) {
      setAccessTokenSource(async () => preview);
      setLoading(false);
      return;
    }

    if (!client) {
      // Nothing to ask and nothing to wait for. The public screens still
      // render, and the guard sends anyone else to sign in.
      setLoading(false);
      return;
    }

    // Asked for on every request rather than captured once: the token expires
    // in an hour and the SDK refreshes it in the background, so a copy taken at
    // mount is a copy that goes stale while she is reading a prompt.
    setAccessTokenSource(async () => {
      const { data } = await client.auth.getSession();
      return data.session?.access_token ?? null;
    });

    void client.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = client.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => subscription.subscription.unsubscribe();
  }, [client]);

  /**
   * Identify on every session, reset on sign-out.
   *
   * **The e-mail travels, and that is a decision rather than a default** —
   * ADR-0022 refused it and was amended on 2026-08-08 to allow it. Without a
   * name, answering "this buyer says the generator is broken" meant taking the
   * UUID to the database before you could even find her session, and every
   * support question paid that toll.
   *
   * It is the identifier, so it goes on `identify` rather than on a capture:
   * PostHog stores person properties on the profile and never on the events,
   * which keeps it out of anything exported by an event query.
   */
  useEffect(() => {
    if (session?.user.id) {
      posthog?.identify(session.user.id, { email: session.user.email });
    }
  }, [session?.user.id, session?.user.email, posthog]);

  const value = useMemo<SessionValue>(
    () => ({
      session,
      loading,
      configured: client !== null,
      signOut: async () => {
        posthog?.reset();
        await client?.auth.signOut();
      }
    }),
    [session, loading, client, posthog]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
